import React from "react";
import {Popover, PopoverButton, PopoverPanel} from "@headlessui/react";
import iso6391 from "iso-639-1";
import SmartIcon from "../SmartIcon.tsx";
import type {ICreditBreakdown, ICreditBreakdownRow} from "../../utils/creditsBreakdown.ts";

// The panel is positioned with CSS and never given `anchor`: headless UI 2.2 portals an anchored panel out of
// the dialog's DOM. Kept inline, Escape and outside clicks close the popover alone and the dialog stays put.

export type CreditEstimatePlacement = "top" | "bottom";

/** `sm` is the Create step's one-line summary; `lg` is the headline figure at the right edge of a cost card. */
export type CreditEstimateSize = "sm" | "lg";

interface CreditEstimateLineProps {
  breakdown: ICreditBreakdown;
  isOverBudget: boolean;
  /** Where the panel opens relative to the info button; `top` when the line sits at a clipped dialog's bottom edge. */
  placement?: CreditEstimatePlacement;
  /**
   * `lg` keeps the big-number look the Add Languages card had before the minutes and the breakdown joined it: the
   * credits in `text-2xl` and the card's accent colour, no "Estimated usage" label because the card's title already
   * names the figure, a line that never shrinks into the card's description, and a panel hanging from the button's
   * RIGHT edge, since the figure sits against the card's right edge where a left-anchored panel would be clipped.
   */
  size?: CreditEstimateSize;
  testId?: string;
}

const languageLabel = (code: string | null): string => (code === null ? "" : iso6391.getName(code) || code);

const rowLabel = (row: ICreditBreakdownRow): string => {
  switch (row.kind) {
    case "transcription":
      return "Transcription only";
    case "translation":
      return languageLabel(row.language);
    case "dubbing":
      return `Dubbing script - ${row.language === null ? "source language" : languageLabel(row.language)}`;
    case "unselected-first-language":
      return "First language (not selected)";
  }
};

export const CreditEstimateLine: React.FC<CreditEstimateLineProps> = (
  {
    breakdown,
    isOverBudget,
    placement = "bottom",
    size = "sm",
    testId,
  }: CreditEstimateLineProps
) => {
  const {quote, rows} = breakdown;
  const isHeadline: boolean = size === "lg";
  const creditsClassName: string = isHeadline
    ? `text-2xl font-semibold leading-8 tabular-nums ${isOverBudget ? "text-red-500" : "text-brandPurple-200"}`
    : `font-semibold tabular-nums ${isOverBudget ? "text-red-500" : "text-white"}`;

  return (
    <div data-testid={testId} className={`flex items-center gap-1.5${isHeadline ? " shrink-0" : ""}`}>
      <p className="text-sm text-n-300">
        {!isHeadline && "Estimated usage "}
        <span className={creditsClassName}>
          {quote.total} credits
        </span>
        {" · "}
        <span className="tabular-nums">{quote.billedMinutes} min of video</span>
      </p>
      {rows.length > 0 && (
        <Popover className="relative inline-flex">
          <PopoverButton
            aria-label="How this estimate is calculated"
            className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full fill-n-400 outline-none transition-colors before:absolute before:-inset-2.5 before:content-[''] hover:fill-n-100 data-[open]:fill-n-100 focus-visible:ring-2 focus-visible:ring-brandPurple-300 focus-visible:ring-offset-2 focus-visible:ring-offset-n-700 motion-reduce:transition-none"
          >
            <SmartIcon name="info-circle" className="h-5 w-5 fill-inherit"/>
          </PopoverButton>
          <PopoverPanel
            data-testid="credit-estimate-breakdown"
            className={`absolute ${isHeadline ? "right-0" : "left-0"} z-10 w-72 rounded-xl border border-n-600 bg-n-800 p-3 text-sm shadow-lg ${
              placement === "top" ? "bottom-full mb-2" : "top-full mt-2"
            }`}
          >
            <ul className="flex flex-col gap-1">
              {rows.map((row: ICreditBreakdownRow) => (
                <li key={`${row.kind}:${row.language ?? ""}`} className="flex justify-between gap-3 text-n-300">
                  <span>{rowLabel(row)}</span>{" "}
                  <span className="shrink-0 tabular-nums text-white">
                    {row.minutes} min × {row.rate} = {row.credits}
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-2 flex justify-between gap-3 border-t border-n-600 pt-2 font-semibold text-white">
              <span>Total</span>{" "}
              <span className="tabular-nums">{quote.total} credits</span>
            </p>
          </PopoverPanel>
        </Popover>
      )}
    </div>
  );
};
